import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "@hassanmojab/react-modern-calendar-datepicker";
import moment from "jalali-moment";
import { FiCalendar } from "react-icons/fi";
import { CalendarDays, X, Check, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  CalendarInput
 * ================================================================ */
const CalendarInput = ({ label, value, onChange }) => {
  const { t } = useTranslation(["user"]);

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  const [calendarPosition, setCalendarPosition] = useState("bottom");

  useEffect(() => {
    if (isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setCalendarPosition(spaceBelow < 320 ? "top" : "bottom");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (date) => {
    if (!date) return "";
    const m = moment
      .from(`${date.year}/${date.month}/${date.day}`, "fa", "YYYY/MM/DD")
      .locale("fa");
    return m.format("jYYYY/jMM/jDD");
  };

  return (
    <div ref={ref} className="relative w-full">
      <label className="text-[11px] font-bold text-slate-500 dark:!text-slate-400 mb-1.5 block tracking-wide">
        {label}
      </label>

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`
          group flex items-center justify-between gap-2 w-full
          px-3 py-2.5 rounded-xl text-sm
          transition-all duration-200
          ${
            isOpen
              ? "bg-amber-300/10 border-amber-300/60 text-slate-800 dark:!text-amber-100 ring-2 ring-amber-300/40"
              : "bg-white/70 dark:!bg-slate-800/60 border-teal-500/20 dark:!border-teal-400/20 text-slate-700 dark:!text-slate-200 hover:border-amber-300/60 hover:bg-amber-300/5"
          }
          border
        `}
      >
        <span className="flex items-center gap-2 min-w-0">
          <CalendarDays
            size={14}
            className={`
              shrink-0 transition-colors duration-200
              ${value ? "text-amber-500" : "text-teal-600/70 dark:!text-teal-300/70"}
            `}
          />
          <span className={`truncate tabular-nums ${!value ? "text-slate-400 dark:!text-slate-500" : "font-semibold"}`}>
            {value ? formatDate(value) : t("dateFilter.selectDate")}
          </span>
        </span>

        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onChange(null);
              }
            }}
            aria-label={t("dateFilter.clearDate")}
            className="shrink-0 h-5 w-5 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
          >
            <X size={12} />
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`
            absolute z-[60] right-0 left-0
            rounded-xl overflow-hidden
            bg-white dark:!bg-slate-900
            border border-teal-500/25 dark:!border-teal-400/20
            shadow-[0_20px_60px_-20px_rgba(15,118,110,0.5)]
            dark:!shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]
            ${
              calendarPosition === "top"
                ? "bottom-full mb-2"
                : "top-full mt-2"
            }
          `}
        >
          {/* خط طلایی */}
          <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

          <div className="flex justify-end p-2 pb-0">
            <button
              onClick={() => setIsOpen(false)}
              aria-label={t("dateFilter.closeCalendar")}
              className="h-7 w-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
            >
              <X size={14} />
            </button>
          </div>

          <div className="p-2 pt-0">
            <Calendar
              value={value}
              onChange={(val) => {
                onChange(val);
                setIsOpen(false);
              }}
              locale="fa"
              calendarClassName="custom-calendar"
              colorPrimary="#14B8A6"
              shouldHighlightWeekends
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================
 *  DateFilterBox
 * ================================================================ */
const DateFilterBox = ({
  selectedMinDate,
  selectedMaxDate,
  setSelectedMinDate,
  setSelectedMaxDate,
  onClearFilters,
}) => {
  const { t } = useTranslation(["user"]);

  const [isBoxOpen, setIsBoxOpen] = useState(false);
  const boxRef = useRef();

  /* ---------- close on outside click ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setIsBoxOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- close on Escape ---------- */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setIsBoxOpen(false);
    };
    if (isBoxOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isBoxOpen]);

  const hasDateFilter = selectedMinDate || selectedMaxDate;

  const handleClear = () => {
    setSelectedMinDate(null);
    setSelectedMaxDate(null);
    if (onClearFilters) onClearFilters();
    setIsBoxOpen(false);
  };

  return (
    <div className="relative inline-block" ref={boxRef}>

      {/* ---------- Trigger Button ---------- */}
      <button
        type="button"
        onClick={() => setIsBoxOpen((prev) => !prev)}
        aria-expanded={isBoxOpen}
        className={`
          group relative
          flex items-center gap-2
          h-10 px-3 sm:px-4 rounded-xl
          text-xs sm:text-sm font-semibold
          transition-all duration-300
          whitespace-nowrap
          ${isBoxOpen
            ? "text-amber-900 dark:!text-amber-100 bg-gradient-to-l from-amber-300/70 to-amber-400/40 border border-amber-300/60 shadow-[0_4px_16px_-6px_rgba(251,191,36,0.6)]"
            : hasDateFilter
              ? "text-amber-700 dark:!text-amber-200 bg-gradient-to-l from-amber-300/30 to-amber-400/10 border border-amber-300/40 hover:from-amber-300/50 hover:to-amber-400/20"
              : "text-teal-700 dark:!text-amber-200 bg-white/60 dark:!bg-white/5 border border-teal-500/20 dark:!border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10"
          }
          backdrop-blur-md
          hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.5)]
          hover:scale-[1.03] active:scale-95
        `}
      >
        <FiCalendar
          size={15}
          className={`shrink-0 ${hasDateFilter ? "text-amber-500" : "text-teal-600/80 dark:!text-teal-300/80"}`}
        />
        <span className="hidden sm:inline">{t("dateFilter.triggerDesktop")}</span>
        <span className="sm:hidden">{t("dateFilter.triggerMobile")}</span>

        {/* نشانگر فیلتر فعال */}
        {hasDateFilter && (
          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-400 border-2 border-white dark:!border-slate-900 shadow-[0_0_6px_rgba(251,191,36,0.9)]" />
        )}
      </button>

      {/* ---------- Popup ---------- */}
      <div
        className={`
          absolute top-full mt-3 right-0 rtl:right-0 ltr:left-0
          w-[320px] max-w-[92vw]
          z-50
          origin-top
          transform transition-all duration-200 ease-out
          ${isBoxOpen
            ? "opacity-100 scale-100 translate-y-0 visible"
            : "opacity-0 scale-95 -translate-y-2 invisible pointer-events-none"
          }
        `}
      >
        <div
          className="
            relative rounded-2xl overflow-hidden
            bg-white dark:!bg-slate-900
            border border-teal-500/25 dark:!border-teal-400/20
            shadow-[0_20px_60px_-20px_rgba(15,118,110,0.55)]
            dark:!shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]
          "
        >
          {/* خط طلایی بالا */}
          <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

          {/* هدر پاپ‌آپ */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-teal-500/10 dark:!border-teal-400/10">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                <CalendarDays size={13} className="text-amber-400" />
              </span>
              <h4 className="text-sm font-bold text-slate-800 dark:!text-amber-100">
                {t("dateFilter.popupTitle")}
              </h4>
            </div>
            <button
              onClick={() => setIsBoxOpen(false)}
              aria-label={t("dateFilter.close")}
              className="h-7 w-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
            >
              <X size={15} />
            </button>
          </div>

          {/* بدنه */}
          <div className="flex flex-col gap-3 p-4">
            <CalendarInput
              label={t("dateFilter.fromDate")}
              value={selectedMinDate}
              onChange={setSelectedMinDate}
            />

            <CalendarInput
              label={t("dateFilter.toDate")}
              value={selectedMaxDate}
              onChange={setSelectedMaxDate}
            />

            {/* دکمه‌های اکشن */}
            <div className="flex items-center gap-2 pt-2 border-t border-teal-500/10 dark:!border-teal-400/10">
              <button
                type="button"
                onClick={() => setIsBoxOpen(false)}
                className="
                  flex-1 flex items-center justify-center gap-1.5
                  py-2.5 px-3 rounded-xl
                  text-xs sm:text-sm font-bold
                  text-white
                  bg-gradient-to-l from-teal-700 to-teal-500
                  shadow-[0_6px_20px_-6px_rgba(15,118,110,0.7)]
                  hover:from-teal-600 hover:to-teal-400
                  hover:shadow-[0_8px_24px_-6px_rgba(20,184,166,0.85)]
                  active:scale-[0.98]
                  transition-all duration-300
                "
              >
                <Check size={14} />
                {t("dateFilter.apply")}
              </button>

              <button
                type="button"
                onClick={handleClear}
                disabled={!hasDateFilter}
                className={`
                  flex items-center justify-center gap-1.5
                  px-3 py-2.5 rounded-xl
                  text-xs font-bold
                  transition-all duration-300
                  whitespace-nowrap
                  ${hasDateFilter
                    ? "text-rose-600 dark:!text-rose-300 border border-rose-400/40 dark:!border-rose-400/30 bg-rose-500/5 hover:bg-rose-500/15 hover:shadow-[0_4px_16px_-6px_rgba(244,63,94,0.6)] active:scale-95"
                    : "text-slate-400 dark:!text-slate-600 bg-slate-100/50 dark:!bg-slate-800/40 border border-slate-200/50 dark:!border-slate-700/50 cursor-not-allowed"
                  }
                `}
              >
                <RotateCcw size={13} />
                <span className="hidden sm:inline">{t("dateFilter.clear")}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateFilterBox;